import { Component, Injector, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, EventEmitter, Output, ViewChildren, QueryList } from '@angular/core';
import { FormAttributeComponent } from '../form-attribute/form-attribute.component';
import { ActivatedRoute, Router } from '@angular/router';
import { environment, LogService } from '@eview/core';
import { UserClickedMap } from '@eview/core/store/actions/map.actions';
import {
  EPostsActions,
  SubmitPostFailure,
  SubmitPostSuccess
} from '@eview/core/store/actions/posts.actions';
import { PostEditorBaseComponent } from '@eview/features/ui/post-editor/post-editor-base.component';
import { ofType } from '@ngrx/effects';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { ngbDateCustomFmt } from '../../ngb-date-custom-fmt';
import { Subscription } from 'rxjs';
import { FormAttributeType } from '@eview/core/domain/form/form-attribute';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { Form, sortFormsDefaultFirst } from '@eview/core/domain/form/form';
import { ListForms } from '@eview/core/store/actions/forms.actions';
import {
  ECustomActions, ShowPostDetail
} from '../../custom.store';
import { PostType } from '@eview/core/domain/post/post';

@Component({
  selector: 'eview-post-editor',
  templateUrl: 'post-editor-page.component.html',
  styleUrls: ['post-editor-page.component.scss'],
  providers: [{ provide: NgbDateParserFormatter, useClass: ngbDateCustomFmt }]
})
export class PostEditorPageComponent extends PostEditorBaseComponent
  implements OnInit, OnDestroy, OnChanges {
  /** Modal must not overwrite NgRx form attributes used by the format-fields screen. */
  override isolateFormAttributes = true;

  @Input() formId: number = 1;
  @Input() type: string;
  @Output() modalCloseEvent = new EventEmitter<string>();
  @ViewChildren(FormAttributeComponent) formAttributeComponents: QueryList<FormAttributeComponent>;
  private subs1: Subscription;
  FormAttributeType = FormAttributeType;
  formList: Form[];
  selectedFormId: number;
  PostType = PostType;
  showFields: boolean = false;
  private lastRequestedFormAttributesId: number | null = null;
  private lastLoadedPostId: number | null = null;

  constructor(
    injector: Injector,
    private logService: LogService,
    route: ActivatedRoute
  ) {
    super(injector, +route.snapshot.params.id);
    this.subs1 = new Subscription();
  }

  ngOnInit() {
    super.ngOnInit();
    this.selectedFormId = this.formId ? this.formId : environment.form.id;
    this.store.dispatch(new UserClickedMap({ actual: null }));
    this.subs1.add(
      this.actions$
        .pipe(ofType<SubmitPostSuccess>(EPostsActions.SubmitPostSuccess))
        .subscribe(action => {
          if (action.payload && action.payload.isEditing) {
            this.store.dispatch({
              type: ECustomActions.UpdatedPost,
              payload: action.payload
            });
            this.store.dispatch(new ShowPostDetail(action.payload));
          } else {
            this.store.dispatch(new UserClickedMap({ actual: { lat: null, lon: null } }));
          }
          this.modalCloseEvent.emit('success');
        })
    );
    this.subs1.add(
      this.actions$
        .pipe(ofType<SubmitPostFailure>(EPostsActions.SubmitPostFailure))
        .subscribe(() => {
          this.logService.error(
            'An error occourred while submitting the post.'
          );
        })
    );

    this.subs1.add(
      this.store
        .select(selectForms)
        .subscribe(forms => {
          this.formList = sortFormsDefaultFirst(
            forms.results.filter(form => !form.disabled)
          );
        })
    );

    this.store.dispatch(new ListForms(true));
    // NgbModal sets postId/formId on componentInstance after ngOnInit — defer first load.
    setTimeout(() => {
      if (!this.postId) {
        this.initModalEditor();
      }
    }, 0);
  }

  /**
   * Call after assigning postId/formId on the modal instance (create + edit).
   */
  initModalEditor(): void {
    if (this.postId != null && this.postId !== '') {
      this.id = Number(this.postId);
      this.lastLoadedPostId = this.id;
    }
    const formId = Number(this.formId || this.selectedFormId || environment.form.id);
    if (!formId || Number.isNaN(formId)) {
      return;
    }
    this.selectedFormId = formId;
    this.formId = formId;
    this.lastRequestedFormAttributesId = null;
    this.loadModalFormAttributes(formId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.formId && changes.formId.currentValue != null) {
      this.onFormChange(Number(changes.formId.currentValue));
    }
  }

  onFormChange(selectedFormId: number) {
    const formId = Number(selectedFormId);
    if (!formId || Number.isNaN(formId)) {
      return;
    }
    const allowed = this.formList?.map(f => Number(f.id)) ?? [];
    if (allowed.length && !allowed.includes(formId)) {
      return;
    }
    const postId = this.postId != null && this.postId !== '' ? Number(this.postId) : null;
    if (
      this.lastRequestedFormAttributesId === formId &&
      this.lastLoadedPostId === postId
    ) {
      return;
    }
    this.lastRequestedFormAttributesId = formId;
    this.lastLoadedPostId = postId;
    this.selectedFormId = formId;
    this.formId = formId;
    if (postId) {
      this.id = postId;
    }
    this.loadModalFormAttributes(formId);
  }

  private loadModalFormAttributes(formId: number): void {
    this.showFields = false;
    this.loadEditorFormAttributes(formId, () => {
      this.showFields = true;
    });
  }

  get canShowFormFields(): boolean {
    return !!this.formAttributesEx && this.showFields;
  }

  onCancelClick(param: string) {
    this.modalCloseEvent.emit(param);
  }

  closeModal(param: string) {
    this.modalCloseEvent.emit(param);
  }

  onSubmitClick() {
    if (this.formAttributeComponents) {
      this.formAttributeComponents.forEach(component => {
        component.markAsTouched();
        // Flush visible datetime-local digits into FormAttribute (no TZ rewrite).
        if (component.FormAttribute?.type === FormAttributeType.DateTime) {
          component.onValueChanged();
        }
      });
    }
    setTimeout(() => {
      super.onSubmitClick();
    }, 0);
  }

  ngOnDestroy() {
    this.formList = null;
    this.subs1.unsubscribe();
  }
}
